import { Linkedin } from "lucide-react";
import { openExternalLink } from "@/lib/openExternalLink";
import { useSiteContent } from "@/hooks/useSiteContent";

export default function About() {
  const c = useSiteContent();
  return (
    <section id="team" className="section bg-[var(--bone)]">
      <div className="container-pl">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-5">
            <div className="overline" data-testid="about-overline">About Padma</div>
            <h2 className="font-display font-bold text-3xl md:text-5xl mt-3 leading-tight">
              Four decades. One conviction:{" "}
              <span className="text-[var(--forest)]">teacher aides matter.</span>
            </h2>

            <div className="mt-8 rounded-2xl overflow-hidden border border-[var(--border)] bg-white">
              <img
                src="/padma.jpeg"
                alt="Padma Krishnan — Founder, Paraprofessionals Learning Community"
                className="w-full h-[420px] object-cover"
                loading="lazy"
                data-testid="about-padma-image"
              />
              <div className="p-5">
                <div className="font-display font-semibold text-xl">Padma Krishnan</div>
                <div className="text-sm text-[var(--slate)] mt-0.5">
                  {c.padma_role}
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-7" data-testid="about-padma-bio">
            <div className="overline">About me</div>
            <h3 className="font-display font-semibold text-2xl md:text-3xl mt-3 leading-snug">
              Building confidence, strengthening capability, supporting equitable outcomes for all
              learners.
            </h3>

            <div className="mt-7 space-y-5 text-[var(--charcoal)] text-base md:text-[1.05rem] leading-relaxed">
              {(c.padma_bio || "")
                .split(/\n\s*\n/)
                .filter((p) => p.trim())
                .map((paragraph, idx, arr) => (
                  <p
                    key={idx}
                    className={
                      idx === arr.length - 1 ? "text-[var(--forest)] font-medium" : undefined
                    }
                  >
                    {paragraph.trim()}
                  </p>
                ))}
            </div>

            <div className="mt-9 grid grid-cols-3 gap-4 max-w-lg" data-testid="about-credentials">
              <Credential value="40+" label="Years in education" />
              <Credential value="10 yrs" label="Leading the Teacher Aide PLC" />
              <Credential value="MOE" label="Accredited PLD Facilitator" />
            </div>

            <div className="mt-10" data-testid="about-coaching-accreditations">
              <div className="overline">Professional Coaching Accreditations</div>
              <p className="mt-3 text-[var(--slate)] leading-relaxed max-w-2xl">
                Alongside her educational leadership, Padma is an internationally
                accredited coach. Coaching skills are essential across every sector of
                education — from classroom practice to whole-school leadership — and
                underpin everything she brings to her professional learning work.
              </p>

              <div className="mt-6 grid sm:grid-cols-2 gap-5">
                <div className="card !p-6 flex items-start gap-5" data-testid="accreditation-gci">
                  <div className="w-24 h-24 shrink-0 rounded-lg bg-white border border-[var(--border)] p-2 flex items-center justify-center">
                    <img
                      src="/gci-logo.jpg"
                      alt="Growth Coaching International — Accredited Coach"
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-[var(--charcoal)]">
                      Growth Coaching International
                    </div>
                    <div className="text-xs text-[var(--forest)] font-display font-semibold uppercase tracking-wider mt-0.5">
                      Accredited Coach
                    </div>
                    <p className="mt-2 text-sm text-[var(--slate)] leading-relaxed">
                      An internationally recognised coaching accreditation focused on
                      evidence-based coaching for education.
                    </p>
                  </div>
                </div>

                <div className="card !p-6 flex items-start gap-5" data-testid="accreditation-emcc">
                  <div className="w-24 h-24 shrink-0 rounded-lg bg-white border border-[var(--border)] p-2 flex items-center justify-center">
                    <img
                      src="/emcc-logo.jpg"
                      alt="EMCC Global — Individual Accreditation (EIA Foundation)"
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-[var(--charcoal)]">
                      EMCC Global
                    </div>
                    <div className="text-xs text-[var(--forest)] font-display font-semibold uppercase tracking-wider mt-0.5">
                      Individual Accreditation · EIA Foundation
                    </div>
                    <p className="mt-2 text-sm text-[var(--slate)] leading-relaxed">
                      The European Mentoring &amp; Coaching Council's Individual
                      Accreditation — one of the most respected coaching credentials
                      worldwide.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <a
              href={c.contact_linkedin}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => openExternalLink(e, c.contact_linkedin)}
              className="btn btn-primary mt-8"
              data-testid="about-linkedin-link"
            >
              <Linkedin size={18} /> View Padma&apos;s LinkedIn profile
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Credential({ value, label }) {
  return (
    <div className="card !p-5">
      <div className="font-display font-bold text-2xl text-[var(--forest)]">{value}</div>
      <div className="text-xs text-[var(--slate)] mt-1 leading-snug">{label}</div>
    </div>
  );
}
